import { useEffect, useState } from 'react';
import { resolvePhotoUrl } from '@/utils/storageUtils';

type ClientLike = { id: string; photo?: string | null; photo_url?: string | null };

/**
 * Resolve all client photo storage paths / expired signed URLs into fresh
 * signed URLs. Returns a `getClientPhoto(id)` accessor plus the raw map.
 *
 * Use everywhere a client Avatar is rendered — otherwise raw storage paths
 * are passed to <img src> and the fallback icon shows instead of the photo.
 */
export function useClientPhotos(clients: ClientLike[]) {
  const [resolvedPhotos, setResolvedPhotos] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      const entries = await Promise.all(
        clients
          .filter((c) => c.photo || c.photo_url)
          .map(async (c) => {
            const url = await resolvePhotoUrl(c.photo || c.photo_url || '');
            return url ? ([c.id, url] as const) : null;
          })
      );
      if (cancelled) return;
      const map: Record<string, string> = {};
      for (const entry of entries) {
        if (entry) map[entry[0]] = entry[1];
      }
      setResolvedPhotos(map);
    };
    if (clients.length > 0) resolve();
    else setResolvedPhotos({});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients.map((c) => `${c.id}:${c.photo || c.photo_url || ''}`).join('|')]);

  const getClientPhoto = (clientId: string): string | undefined =>
    resolvedPhotos[clientId] || undefined;

  return { getClientPhoto, resolvedPhotos };
}
